(() => {
  if (window.__rtnuNavStickyBound) return;
  window.__rtnuNavStickyBound = true;

  const HEADER_SELECTOR = '.rtnu-nav-header';
  const TOPBAR_SELECTORS = ['.wayfx-topbar', '.hp-top-bar', '.ab30stack__topbar'];
  const SCROLL_HIDE_THRESHOLD = 120;
  const SCROLL_DELTA_THRESHOLD = 4;
  const NO_TRANSITION_CLASS = 'rtnu-nav--no-transition';
  const SCROLL_UP_CLASS = 'rtnu-nav--scrolling-up';
  const SCROLL_DOWN_CLASS = 'rtnu-nav--scrolling-down';

  const boundHeaders = new WeakSet();
  let headers = [];
  let baseOffset = 0;
  let ticking = false;
  let resizeTimer = null;
  let topbarResizeObserver = null;
  let lastScrollY = window.scrollY || window.pageYOffset || 0;

  const getScrollY = () => window.scrollY || window.pageYOffset || 0;

  const isElementVisible = (element) => {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;
    return element.getBoundingClientRect().height > 0;
  };

  const getTopbarElements = () => {
    const elements = [];
    TOPBAR_SELECTORS.forEach((selector) => {
      const topbar = document.querySelector(selector);
      if (topbar) elements.push(topbar);
    });
    return elements;
  };

  const computeBaseOffset = () =>
    getTopbarElements().reduce((sum, element) => {
      if (!isElementVisible(element)) return sum;
      return sum + element.getBoundingClientRect().height;
    }, 0);

  const setOffsetVariable = (value) => {
    const roundedValue = Math.max(0, Math.round(value));
    document.documentElement.style.setProperty('--rtnu-nav-offset', `${roundedValue}px`);
  };

  const setHeaderHeightVariables = () => {
    const primaryHeader = headers[0];
    if (!primaryHeader) return;

    const desktopRegion = primaryHeader.querySelector('.rtnu-nav-desktop');
    const mobileBar = primaryHeader.querySelector('.rtnu-nav-mobile__bar');

    const desktopHeight = isElementVisible(desktopRegion) ? Math.round(desktopRegion.getBoundingClientRect().height) : 0;
    const mobileHeight = isElementVisible(mobileBar) ? Math.round(mobileBar.getBoundingClientRect().height) : 0;
    const fallbackHeight = Math.round(primaryHeader.getBoundingClientRect().height);

    const headerHeight = desktopHeight || fallbackHeight;

    document.body.style.setProperty('--rtnu-nav-header-height', `${headerHeight}px`);
    document.body.style.setProperty('--rtnu-nav-header-mobile-height', `${mobileHeight}px`);
  };

  const setHeaderState = (header, direction) => {
    if (direction === 'down') {
      header.classList.add(SCROLL_DOWN_CLASS);
      header.classList.remove(SCROLL_UP_CLASS);
      return;
    }
    header.classList.add(SCROLL_UP_CLASS);
    header.classList.remove(SCROLL_DOWN_CLASS);
  };

  const isHeaderInteractionOpen = (header) => {
    const desktopNav = header.querySelector('.rtnu-nav-desktop');
    if (desktopNav?.classList.contains('is-menu-open')) return true;

    const mobilePanel = header.querySelector('.rtnu-nav-mobile__panel');
    if (mobilePanel && !mobilePanel.hidden) return true;

    return document.body.classList.contains('rtnu-nav-mobile-open');
  };

  const bindHeaders = () => {
    headers = Array.from(document.querySelectorAll(HEADER_SELECTOR));
    headers.forEach((header) => {
      if (boundHeaders.has(header)) return;
      boundHeaders.add(header);
      header.classList.add(SCROLL_UP_CLASS);
      header.classList.remove(SCROLL_DOWN_CLASS);
    });
  };

  const applyScrollDirection = () => {
    const currentScrollY = getScrollY();
    const scrollDelta = currentScrollY - lastScrollY;
    const dynamicOffset = Math.max(0, baseOffset - currentScrollY);

    setOffsetVariable(dynamicOffset);

    if (currentScrollY <= 0) {
      headers.forEach((header) => setHeaderState(header, 'up'));
      lastScrollY = currentScrollY;
      return;
    }

    if (Math.abs(scrollDelta) < SCROLL_DELTA_THRESHOLD) {
      lastScrollY = currentScrollY;
      return;
    }

    headers.forEach((header) => {
      if (scrollDelta > 0 && currentScrollY > SCROLL_HIDE_THRESHOLD && !isHeaderInteractionOpen(header)) {
        setHeaderState(header, 'down');
      } else if (scrollDelta < 0) {
        setHeaderState(header, 'up');
      }
    });

    lastScrollY = currentScrollY;
  };

  const runScrollTick = () => {
    if (ticking) return;
    ticking = true;

    window.requestAnimationFrame(() => {
      applyScrollDirection();
      ticking = false;
    });
  };

  const recalcOffsetAndSync = ({ noTransition = false } = {}) => {
    baseOffset = computeBaseOffset();
    setHeaderHeightVariables();

    if (noTransition) {
      headers.forEach((header) => header.classList.add(NO_TRANSITION_CLASS));
    }

    applyScrollDirection();

    if (noTransition) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          headers.forEach((header) => header.classList.remove(NO_TRANSITION_CLASS));
        });
      });
    }
  };

  const onResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      recalcOffsetAndSync({ noTransition: true });
    }, 120);
  };

  const observeTopbars = () => {
    if (!('ResizeObserver' in window)) return;
    if (topbarResizeObserver) topbarResizeObserver.disconnect();

    topbarResizeObserver = new ResizeObserver(() => {
      recalcOffsetAndSync({ noTransition: true });
    });

    getTopbarElements().forEach((element) => topbarResizeObserver.observe(element));
  };

  const init = () => {
    bindHeaders();
    if (!headers.length) return;

    recalcOffsetAndSync({ noTransition: true });
    observeTopbars();

    window.addEventListener('scroll', runScrollTick, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });
    window.addEventListener(
      'load',
      () => {
        bindHeaders();
        recalcOffsetAndSync({ noTransition: true });
        observeTopbars();
      },
      { once: true }
    );

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      bindHeaders();
      recalcOffsetAndSync({ noTransition: true });
      observeTopbars();
    });

    document.addEventListener('shopify:section:load', () => {
      bindHeaders();
      recalcOffsetAndSync({ noTransition: true });
      observeTopbars();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
